<template>
  <div class="w-screen h-screen lg:p-10 bg-pure-white overflow-hidden">
    <div class="bl-auth-background px-4 lg:p-0">
      <div class="bl-auth-card rounded-3xl bg-pure-white p-6 lg:p-14">
        <form
          v-if="step === 'input_email_and_password'"
          @submit.prevent="onContinue"
        >
          <div class="text-center space-y-3 mb-6 lg:w-128">
            <div class="font-logo text-light-purple font-bold text-4xl">
              Blüm
            </div>
            <BlTextBody2
              text="Streamline your ABA therapy data collection process and make your sessions more effective."
            />
          </div>

          <BlInputText
            class="mb-4"
            :input="emailInput"
            @changed="inputHandler"
          />
          <BlInputText
            class="mb-4"
            :input="passwordInput"
            @changed="inputHandler"
          />

          <BlInputButton
            class="w-full"
            buttonType="submit"
            :disabled="!emailInput.model || !passwordInput.model"
            :loading="submitLoading"
            label="Sign in and continue to reset password"
          />
        </form>

        <form v-else @submit.prevent="signin">
          <div class="mb-8 lg:w-128">
            <BlTextTitle4 class="mb-2" text="Change your password" />
            <BlTextBody2
              text="You must change your password to continue. Please enter your new password to keep your personal information safe."
            />
          </div>

          <BlInputText
            :input="newPasswordInput"
            @changed="inputHandler"
            class="mb-4"
          />

          <div class="grid grid-cols-3 mb-4">
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="passwordHasSixChar ? 'bg-success-400' : 'bg-grey-300'"
              />
              <div class="text-xs">6 characters minimum</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="passwordHasUppercase ? 'bg-success-400' : 'bg-grey-300'"
              />
              <div class="text-xs">1 uppercase character</div>
            </div>
          </div>
          <div class="grid grid-cols-3">
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="passwordHasLowescase ? 'bg-success-400' : 'bg-grey-300'"
              />
              <div class="text-xs">1 lowercase character</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="passwordHasNumber ? 'bg-success-400' : 'bg-grey-300'"
              />
              <div class="text-xs">1 number</div>
            </div>
          </div>

          <div class="flex items-center justify-end gap-4">
            <BlInputButton
              @clicked="step = 'input_email_and_password'"
              type="plain"
              label="Cancel"
            />
            <BlInputButton
              buttonType="submit"
              :disabled="!verifiedNewPassword || submitLoading"
              label="Update"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputText from "../BlInputText.vue";
import BlInputButton from "../BlInputButton.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "SetupAccount",
  components: {
    BlTextBody2,
    BlInputText,
    BlInputButton,
    BlTextTitle4,
    BlIcon
  },
  data() {
    return {
      submitLoading: false,
      step: "input_email_and_password",
      signInToken: "",
      emailInput: {
        placeholder: "Type your email here",
        label: "Email",
        inputType: "email",
        model: "",
        error: ""
      },
      passwordInput: {
        placeholder: "Type your password here",
        label: "Password",
        inputType: "password",
        model: "",
        error: ""
      },
      newPasswordInput: {
        placeholder: "Type your new password",
        label: "New password",
        model: "",
        inputType: "password",
        error: ""
      }
    };
  },
  computed: {
    passwordHasSixChar() {
      return this.newPasswordInput.model.length >= 6;
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.newPasswordInput.model);
    },
    passwordHasLowescase() {
      return /[a-z]/.test(this.newPasswordInput.model);
    },
    passwordHasNumber() {
      return /\d/.test(this.newPasswordInput.model);
    },
    verifiedNewPassword() {
      return (
        this.passwordHasSixChar &&
        this.passwordHasUppercase &&
        this.passwordHasLowescase &&
        this.passwordHasNumber
      );
    }
  },
  mounted() {
    this.signInToken = this.$route.params.token;
  },
  methods: {
    ...mapActions(["createCurrentUser", "showToast"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
      payload.input.error = "";
    },
    onContinue() {
      this.passwordInput.error = "";
      const params = {
        email: this.emailInput.model,
        password: this.passwordInput.model
      };
      this.submitLoading = true;
      this.$http.plain
        .post("/api/v1/users/check_password", params)
        .then(response => {
          this.submitLoading = false;
          this.step = "input_new_password";
          const message = "Email and password has been verified";
          this.showToast({ message, type: "success" });
        })
        .catch(error => {
          this.submitLoading = false;
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.error) ||
            "Unable to reset password.";
          this.passwordInput.error = message;
          this.showToast({ message, type: "error" });
        });
    },
    signin() {
      const params = {
        email: this.emailInput.model,
        password: this.passwordInput.model,
        new_password: this.newPasswordInput.model,
        sign_in_token: this.signInToken
      };
      this.submitLoading = true;
      this.$http.plain
        .post("/signin", params)
        .then(response => this.signinSuccessful(response))
        .catch(error => this.signinFailed(error));
    },
    async signinSuccessful(response) {
      if (!response.data.csrf) {
        this.signinFailed(response);
        return;
      }
      localStorage.setItem("csrf", response.data.csrf);
      localStorage.setItem("access", response.data.access);
      await this.createCurrentUser(response.data.user);
      const title = "The password has been updated";
      this.showToast({ title, message: "", type: "success" });
      location.replace("/clients");
      this.submitLoading = false;
    },
    signinFailed(error) {
      const err = error.response && error.response.data;
      const message = err || "Setup account failed";
      this.showToast({ title: "", message, type: "error" });
      localStorage.removeItem("csrf");
      localStorage.removeItem("access");
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/auth/SetupAccount.vue